import type { QuoteFieldDefinitionRecord } from './types';
import {
  createSystemFieldRegistry,
  createUnifiedFieldRegistry,
} from './unifiedFieldRegistry';

export interface ReadOnlyBusinessFieldDescriptor {
  label: string;
  source: string;
  available: boolean;
}

export type BusinessFieldBindingStatus =
  | 'bound'
  | 'read-only'
  | 'unbound'
  | 'not-applicable';
export interface BusinessFieldBinding {
  storagePath?: string;
  valueResolver: string;
  writeCommand?: string;
  permissionResource?: string;
  readActionId?: string;
  editActionId?: string;
  exportActionId?: string;
  exportFeatureId?: string;
  placements: string[];
  snapshotPolicy: 'live-master' | 'freeze-on-publication' | 'not-applicable';
  calculationRef?: string;
  status: BusinessFieldBindingStatus;
}

export interface ReadOnlyBusinessFieldGroup {
  object: string;
  owner: string;
  fields: ReadOnlyBusinessFieldDescriptor[];
}

/** Mechanical inventory of fields already present in the Disposable 3.x pages/types. */
export const READ_ONLY_OBJECT_DIRECTORY: ReadOnlyBusinessFieldGroup[] = [
  {
    object: '设备（设备管理）',
    owner: 'device',
    fields: [
      ['设备编号', 'DeviceCatalogRecord.id'],
      ['设备名称', 'DeviceCatalogRecord.name'],
      ['SKU', 'DeviceCatalogRecord.sku'],
      ['吉客云编号', 'DeviceCatalogRecord.jkyCode'],
      ['设备分类', 'DeviceCatalogRecord.category'],
      ['货品属性', 'DeviceCatalogRecord.deviceAttribute'],
      ['采购属性', 'DeviceCatalogRecord.procurementAttribute'],
      ['重量（kg）', 'DeviceCatalogRecord.weight'],
      ['排序', 'DeviceCatalogRecord.sortOrder'],
      ['状态', 'DeviceCatalogRecord.status'],
      ['备注', 'DeviceCatalogRecord.remark'],
      ['租金', 'DeviceCatalogRecord.rentalPrice'],
      ['最终买断价', 'DeviceCatalogRecord.taxInclusivePlanPrice'],
      ['单价', 'DeviceCatalogRecord.unitPrice'],
      [
        '零售价（含13%专票增值税，人工）',
        'DeviceCatalogRecord.retailPriceTax13',
      ],
      ['动态成本', 'DeviceCatalogRecord.dynamicCost'],
      ['赔付价格（人工）', 'DeviceCatalogRecord.compensationPrice'],
      ['图片', 'DeviceCatalogRecord.image'],
      ['图库', 'DeviceCatalogRecord.gallery'],
      ['图片资产', 'DeviceCatalogRecord.images'],
      ['主图标识', 'DeviceCatalogRecord.primaryImageId'],
      ['替代设备', 'DeviceCatalogRecord.replacementIds'],
      ['分仓库存', 'DeviceCatalogRecord.stockByWarehouse'],
      ['更新时间', 'DeviceCatalogRecord.updatedAt'],
      ['更新时间来源', 'DeviceCatalogRecord.updatedAtSource'],
      ['当前维护位置', 'DeviceCatalogRecord.maintenance'],
    ].map(([label, source]) => ({ label, source, available: true })),
  },
  {
    object: '报价',
    owner: 'quote-line',
    fields: [
      ['报价编号', 'Quote.quoteId'],
      ['版本', 'Quote.version'],
      ['来源报价', 'Quote.sourceQuoteId / sourceVersion'],
      ['来源回执', 'Quote.sourceReceiptId'],
      ['标题', 'Quote.title'],
      ['客户名称', 'Quote.customer.name'],
      ['客户电话', 'Quote.customer.phone'],
      ['客户快照', 'Quote.customer'],
      ['有效期', 'Quote.validUntil'],
      ['业务模式', 'Quote.businessModeCode / businessModeLabelSnapshot'],
      ['押金', 'Quote.deposit'],
      ['整单折扣', 'Quote.overallDiscount'],
      ['报价行', 'Quote.items'],
      ['自定义列', 'Quote.customColumns / customColumnSnapshot'],
      ['状态', 'Quote.status'],
      ['来源', 'Quote.source'],
      ['设备编号', 'QuoteItem.deviceId'],
      ['设备名称', 'QuoteItem.name'],
      ['SKU', 'QuoteItem.sku'],
      ['行来源类型', 'QuoteItem.sourceType'],
      ['行来源编号', 'QuoteItem.sourceId'],
      ['行来源名称', 'QuoteItem.sourceName'],
      ['数量', 'QuoteItem.quantity'],
      ['折扣', 'QuoteItem.discount'],
      ['库存', 'QuoteItem.stock'],
      ['替代设备', 'QuoteItem.replacementDeviceId'],
      ['备注', 'QuoteItem.remark'],
      ['产品最终折后价（含数量行总价）', 'quoteTotals.lineTotal'],
      ['购买最终价（含13%专票增值税）', 'quoteTotals.purchaseFinalPriceTax13'],
      [
        '购买最终价（不含13%专票增值税）',
        'quoteTotals.purchaseFinalPriceExTax13',
      ],
      ['手工字段值', 'QuoteItem.manualFieldValues'],
    ]
      .map(([label, source]) => ({ label, source, available: true }))
      .concat([
        {
          label: '小计租金(元/月)',
          source: 'quoteTotals.quoteMonthlySubtotal',
          available: true,
        },
      ]),
  },
  {
    object: '套餐（套餐管理）',
    owner: 'package',
    fields: [
      ['套餐编号', 'PackageCatalogRecord.id'],
      ['套餐名称', 'PackageCatalogRecord.name'],
      ['关联方案', 'PackageCatalogRecord.planIds'],
      ['排序', 'PackageCatalogRecord.sortOrder'],
      ['产品链接', 'PackageCatalogRecord.productLinks'],
      ['套餐价/月', 'PackageProductLink.packageItemPrice'],
      ['关联数量', 'PackageProductLink.quantity'],
      ['图片', 'PackageCatalogRecord.image'],
      ['灯光图片', 'PackageCatalogRecord.lightImage'],
    ].map(([label, source]) => ({ label, source, available: true })),
  },
  {
    object: '方案（方案管理）',
    owner: 'plan',
    fields: [
      ['方案编号', 'PlanCatalogRecord.id'],
      ['方案名称', 'PlanCatalogRecord.name'],
      ['方案专属折扣', 'PlanCatalogRecord.discount'],
      ['排序', 'PlanCatalogRecord.sortOrder'],
      ['图片', 'PlanCatalogRecord.image'],
      ['案例', 'PlanCatalogRecord.cases'],
    ].map(([label, source]) => ({ label, source, available: true })),
  },
  {
    object: '客户',
    owner: 'customer',
    fields: [
      ['客户编号', 'CustomerCatalogRecord.id'],
      ['客户编码', 'CustomerCatalogRecord.customerNo'],
      ['客户名称', 'CustomerCatalogRecord.name'],
      ['联系人', 'CustomerCatalogRecord.contact'],
      ['联系电话', 'CustomerCatalogRecord.phone'],
      ['客户经理', 'CustomerCatalogRecord.manager'],
      ['状态', 'CustomerCatalogRecord.status'],
    ].map(([label, source]) => ({ label, source, available: true })),
  },
  {
    object: '库存',
    owner: 'inventory',
    fields: [
      ['仓库键', 'WarehouseRecord.key'],
      ['仓库名称', 'WarehouseRecord.name'],
      ['分仓库存', 'DeviceCatalogRecord.stockByWarehouse'],
      ['替代设备', 'DeviceCatalogRecord.replacementIds'],
    ].map(([label, source]) => ({ label, source, available: true })),
  },
  {
    object: '分享回执',
    owner: 'channel',
    fields: [
      ['回执编号', 'ReceiptRecord.id'],
      ['分享编号', 'ReceiptRecord.shareId'],
      ['来源报价及版本', 'ReceiptRecord.sourceQuoteId / sourceVersion'],
      ['回执名称', 'ReceiptRecord.companyOrName'],
      ['回执电话', 'ReceiptRecord.phone'],
      ['备注', 'ReceiptRecord.note'],
      ['选择快照', 'ReceiptRecord.selectionSnapshot'],
      ['状态', 'ReceiptRecord.status'],
      ['转换报价', 'ReceiptRecord.convertedQuoteId'],
      ['打开时间', 'ReceiptRecord.openedAt'],
      ['创建时间', 'ReceiptRecord.createdAt'],
    ].map(([label, source]) => ({ label, source, available: true })),
  },
  {
    object: '临时设备',
    owner: 'temporary',
    fields: [
      ['临时设备编号', 'TemporaryDevice.id'],
      ['临时设备名称', 'TemporaryDevice.name'],
      ['SKU', 'TemporaryDevice.sku'],
      ['临时设备价格', 'TemporaryDevice.price'],
      ['库存', 'TemporaryDevice.stock'],
      ['备注', 'TemporaryDevice.remark'],
      ['操作', 'page:temporary-device.action'],
    ].map(([label, source]) => ({ label, source, available: true })),
  },
];

export interface PermissionBusinessFieldRow {
  id: string;
  label: string;
  owner: string;
  source: 'quote-definition' | 'page';
  available: boolean;
  editable: boolean;
  placements: string[];
  definitionKey?: string;
  note?: string;
  binding: BusinessFieldBinding;
}

/**
 * Stable owner-to-capability bridge. Unknown owners deliberately have no
 * target: consumers must fail closed instead of guessing from Chinese labels.
 */
export interface BusinessFieldPermissionTarget {
  featureId: string;
  readActionId: string;
  editActionId?: string;
  exportActionId?: string;
  exportFeatureId?: string;
}

const OWNER_PERMISSION_TARGETS: Record<
  string,
  BusinessFieldPermissionTarget | undefined
> = {
  'quote-line': {
    featureId: 'quotes',
    readActionId: 'quotes:quote.read',
    editActionId: 'quotes:quote.edit',
    exportActionId: 'quotes:quote.export',
  },
  'quote-header': {
    featureId: 'quotes',
    readActionId: 'quotes:quote.read',
    editActionId: 'quotes:quote.edit',
    exportActionId: 'quotes:quote.export',
  },
  channel: {
    featureId: 'quotes',
    readActionId: 'quotes:quote.read',
    editActionId: 'quotes:quote.share.external',
    exportActionId: 'quotes:quote.export',
  },
  device: {
    featureId: 'devices',
    readActionId: 'devices:catalog.read',
    editActionId: 'devices:catalog.manage',
    exportFeatureId: 'quotes',
    exportActionId: 'quotes:quote.export',
  },
  package: {
    featureId: 'packages',
    readActionId: 'packages:solution.read',
    editActionId: 'packages:solution.manage',
    exportFeatureId: 'quotes',
    exportActionId: 'quotes:quote.export',
  },
  plan: {
    featureId: 'plans',
    readActionId: 'plans:solution.read',
    editActionId: 'plans:solution.manage',
    exportFeatureId: 'quotes',
    exportActionId: 'quotes:quote.export',
  },
  customer: {
    featureId: 'customers',
    readActionId: 'customers:customer.read',
    editActionId: 'customers:customer.manage',
    exportFeatureId: 'quotes',
    exportActionId: 'quotes:quote.export',
  },
  inventory: {
    featureId: 'inventory',
    readActionId: 'inventory:inventory.read',
    editActionId: 'inventory:inventory.adjust',
    exportFeatureId: 'quotes',
    exportActionId: 'quotes:quote.export',
  },
};

export function permissionTargetForBusinessField(
  row: Pick<PermissionBusinessFieldRow, 'owner'>,
): BusinessFieldPermissionTarget | undefined {
  return OWNER_PERMISSION_TARGETS[row.owner];
}

const QUOTE_DEFINITION_OWNERS: Record<string, string> = {
  jky_code: 'device',
  rental_price: 'device',
  tax_inclusive_plan_price: 'device',
  device_unit_price: 'device',
  device_untaxed_plan_price: 'device',
  device_compensation_price: 'device',
  rental_subtotal: 'quote-line',
  unit_price_subtotal: 'quote-line',
  retail_price_tax13: 'device',
  purchase_final_price_tax13: 'quote-line',
  purchase_final_price_ex_tax13: 'quote-line',
  dynamic_cost: 'device',
  dynamic_cost_subtotal: 'device',
  procurement_attribute: 'device',
  device_weight: 'device',
  device_category: 'device',
  device_attribute: 'device',
  warehouse_stock: 'inventory',
  device_image: 'device',
  device_name: 'device',
  device_sku: 'device',
  replacement_device: 'device',
  package_item_price: 'package',
  package_item_monthly_subtotal: 'package',
  package_discount_quote_tax13: 'quote-line',
  package_discounted_unit_price: 'quote-line',
  package_discounted_subtotal: 'quote-line',
  quote_remark: 'quote-line',
  scheme_discount: 'plan',
  scheme_product_price: 'plan',
  scheme_product_subtotal: 'plan',
  quote_title: 'quote-line',
  customer_name: 'customer',
  customer_contact: 'customer',
  customer_phone: 'customer',
  valid_until: 'quote-line',
  business_mode: 'quote-line',
  deposit: 'quote-line',
  overall_discount: 'quote-line',
  quantity: 'quote-line',
};

/** One permission identity for a device master fact and every quote/channel
 * projection of that same fact. The explicit binding replaces label guessing. */
const DEVICE_FIELD_DEFINITION_KEYS: Partial<Record<string, string>> = {
  name: 'device_name',
  sku: 'device_sku',
  image: 'device_image',
  category: 'device_category',
  deviceAttribute: 'device_attribute',
  jkyCode: 'jky_code',
  procurementAttribute: 'procurement_attribute',
  weight: 'device_weight',
  rentalPrice: 'rental_price',
  taxInclusivePlanPrice: 'tax_inclusive_plan_price',
  unitPrice: 'device_unit_price',
  untaxedPlanPrice: 'device_untaxed_plan_price',
  retailPriceTax13: 'retail_price_tax13',
  dynamicCost: 'dynamic_cost',
  compensationPrice: 'device_compensation_price',
  remark: 'device_internal_remark',
  stockByWarehouse: 'warehouse_stock',
  replacementIds: 'replacement_device',
};

/** Device-master facts are writable only through saveDeviceCatalog. Quote
 * lines, history and channel projections consume their frozen snapshot. */
export const DEVICE_MASTER_QUOTE_FIELD_KEYS = new Set([
  'jky_code',
  'rental_price',
  'tax_inclusive_plan_price',
  'device_unit_price',
  'device_untaxed_plan_price',
  'retail_price_tax13',
  'dynamic_cost',
  'device_compensation_price',
  'procurement_attribute',
  'device_weight',
  'device_attribute',
]);

export function isDeviceMasterQuoteField(key: string): boolean {
  return DEVICE_MASTER_QUOTE_FIELD_KEYS.has(key);
}

const DEVICE_FIELD_IDS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(DEVICE_FIELD_DEFINITION_KEYS).map(
      ([property, definitionKey]) => [property, `definition:${definitionKey}`],
    ),
  ),
};

export function permissionFieldIdForDeviceProperty(property: string): string {
  return DEVICE_FIELD_IDS[property] ?? `source:DeviceCatalogRecord.${property}`;
}

export function permissionFieldAliases(fieldId: string): string[] {
  return [fieldId];
}

/** Approved, lossless compatibility aliases. Unknown legacy setting keys are
 * deliberately absent and remain disabled raw settings facts. */
export const LEGACY_SETTINGS_FIELD_BINDINGS: Record<string, string> = {
  '产品表格字段.rental_price':
    permissionFieldIdForDeviceProperty('rentalPrice'),
  '产品表格字段.fields.good_price': 'definition:package_item_price',
  '推单自定义表单.CusName': 'source:CustomerCatalogRecord.name',
  '推单自定义表单.contact': 'source:CustomerCatalogRecord.contact',
  '推单自定义表单.phone': 'source:CustomerCatalogRecord.phone',
};

export function permissionFieldIdForSource(source: string): string {
  const stableManualIds: Record<string, string> = {
    'PackageProductLink.packageItemPrice': 'definition:package_item_price',
    'PlanCatalogRecord.discount': 'definition:scheme_discount',
    'QuoteItem.name': 'definition:device_name',
    'QuoteItem.sku': 'definition:device_sku',
    'QuoteItem.quantity': 'definition:quantity',
    'QuoteItem.discount': 'definition:item_discount',
    'QuoteItem.stock': 'definition:warehouse_stock',
    'QuoteItem.replacementDeviceId': 'definition:replacement_device',
    'quoteTotals.lineTotal': 'definition:product_final_discount_price',
    'Quote.title': 'definition:quote_title',
    'Quote.customer.name': 'definition:customer_name',
    'Quote.customer.phone': 'definition:customer_phone',
    'Quote.validUntil': 'definition:valid_until',
    'Quote.businessModeCode / businessModeLabelSnapshot':
      'definition:business_mode',
    'Quote.deposit': 'definition:deposit',
    'Quote.overallDiscount': 'definition:overall_discount',
  };
  if (stableManualIds[source]) return stableManualIds[source];
  const devicePrefix = 'DeviceCatalogRecord.';
  return source.startsWith(devicePrefix)
    ? permissionFieldIdForDeviceProperty(source.slice(devicePrefix.length))
    : `source:${source}`;
}

export function ownerForQuoteDefinition(key: string): string {
  return QUOTE_DEFINITION_OWNERS[key] ?? 'quote-line';
}

const QUOTE_DEFINITION_BINDINGS: Record<
  string,
  Omit<
    BusinessFieldBinding,
    'permissionResource' | 'readActionId' | 'editActionId' | 'exportActionId'
  >
> = {
  jky_code: {
    storagePath:
      'DeviceCatalogRecord.jkyCode -> QuoteItem.deviceFieldSnapshot.jkyCode',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  rental_price: {
    storagePath:
      'DeviceCatalogRecord.rentalPrice -> QuoteItem.deviceFieldSnapshot.rentalPrice',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  tax_inclusive_plan_price: {
    storagePath:
      'DeviceCatalogRecord.taxInclusivePlanPrice -> QuoteItem.deviceFieldSnapshot.taxInclusivePlanPrice',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_unit_price: {
    storagePath:
      'DeviceCatalogRecord.unitPrice -> QuoteItem.deviceFieldSnapshot.unitPrice',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_untaxed_plan_price: {
    storagePath:
      'DeviceCatalogRecord.untaxedPlanPrice -> QuoteItem.deviceFieldSnapshot.untaxedPlanPrice',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_compensation_price: {
    storagePath:
      'DeviceCatalogRecord.compensationPrice -> QuoteItem.deviceFieldSnapshot.compensationPrice',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  rental_subtotal: {
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'quote-rental-subtotal-v1',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  unit_price_subtotal: {
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'quote-unit-price-subtotal-v1',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  package_discounted_unit_price: {
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'package-discounted-unit-price-v2',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  package_discounted_subtotal: {
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'package-discounted-subtotal-v2',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  item_discount: {
    storagePath: 'QuoteItem.discount',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'updateQuoteItem',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  product_final_discount_price: {
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'final-discounted-relation-line-v2',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  scheme_discount: {
    storagePath: 'PlanCatalogRecord.discount -> QuoteItem.planDiscountSnapshot',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'savePlanCatalog',
    placements: ['方案管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  package_item_price: {
    storagePath:
      'PackageProductLink.packageItemPrice -> QuoteItem.packageItemPriceSnapshot',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'savePackageCatalog',
    placements: [
      '套餐管理（关系层人工维护）',
      '报价字段池',
      '报价行',
      '发布快照',
    ],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  package_item_monthly_subtotal: {
    storagePath:
      'PackageProductLink.packageItemPrice × PackageProductLink.quantity -> QuoteItem relation snapshot',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'package-item-monthly-subtotal-v1',
    placements: [
      '套餐管理（关系层只读小计）',
      '报价字段池',
      '报价行',
      '发布快照',
    ],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  scheme_product_price: {
    storagePath:
      'PackageProductLink.packageItemPrice + PlanCatalogRecord.discount -> QuoteItem.planProductPriceSnapshot',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'plan-product-price-v2',
    placements: ['方案管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  scheme_product_subtotal: {
    storagePath:
      '设备关系层：QuoteItem.planProductPriceSnapshot × QuoteItem.packageRelationQuantitySnapshot -> QuoteItem.planProductSubtotalSnapshot；套餐层/方案层：Σ 下级方案专属价格小计',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'plan-product-subtotal-v2',
    placements: [
      '方案管理（设备关系层只读小计）',
      '方案管理（套餐层只读汇总）',
      '方案管理（方案层只读汇总）',
      '报价字段池',
      '报价行',
      '报价汇总',
      '发布快照',
    ],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  package_discount_quote_tax13: {
    storagePath:
      'QuoteItem.packageDiscountedQuoteSnapshot -> QuoteItem.calculationSnapshots.package_discount_quote_tax13',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'package-discounted-quote-v2',
    placements: ['报价工作台', '报价字段池', '报价汇总', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  retail_price_tax13: {
    storagePath:
      'DeviceCatalogRecord.retailPriceTax13 -> QuoteItem.deviceFieldSnapshot.retailPriceTax13',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  purchase_final_price_tax13: {
    storagePath: 'quoteTotals.purchaseFinalPriceTax13',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  purchase_final_price_ex_tax13: {
    storagePath: 'quoteTotals.purchaseFinalPriceExTax13',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  dynamic_cost: {
    storagePath:
      'DeviceCatalogRecord.dynamicCost -> QuoteItem.deviceFieldSnapshot.dynamicCost',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  dynamic_cost_subtotal: {
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    calculationRef: 'dynamic-cost-subtotal-v1',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  device_image: {
    storagePath:
      'DeviceCatalogRecord.image -> QuoteItem.deviceFieldSnapshot.image',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_name: {
    storagePath: 'DeviceCatalogRecord.name -> QuoteItem.name',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_sku: {
    storagePath: 'DeviceCatalogRecord.sku -> QuoteItem.sku',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  quantity: {
    storagePath: 'QuoteItem.quantity',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'updateQuoteItem',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  warehouse_stock: {
    storagePath:
      'DeviceCatalogRecord.stockByWarehouse -> QuoteItem.deviceFieldSnapshot.stockByWarehouse',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    placements: ['库存', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'read-only',
  },
  replacement_device: {
    storagePath:
      'DeviceCatalogRecord.replacementIds / QuoteItem.replacementDeviceId',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'replaceQuotePackageItem',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_category: {
    storagePath:
      'DeviceCatalogRecord.category -> QuoteItem.deviceFieldSnapshot.category',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_attribute: {
    storagePath:
      'DeviceCatalogRecord.deviceAttribute -> QuoteItem.deviceFieldSnapshot.deviceAttribute',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  procurement_attribute: {
    storagePath:
      'DeviceCatalogRecord.procurementAttribute -> QuoteItem.deviceFieldSnapshot.procurementAttribute',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  device_weight: {
    storagePath:
      'DeviceCatalogRecord.weight -> QuoteItem.deviceFieldSnapshot.weight',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'saveDeviceCatalog',
    placements: ['设备管理', '报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  quote_remark: {
    storagePath: 'QuoteItem.remark',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'updateQuoteItem',
    placements: ['报价字段池', '报价行', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  quote_title: {
    storagePath: 'Quote.title',
    valueResolver: 'resolveQuoteHeaderFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  customer_name: {
    storagePath: 'Quote.customer.name',
    valueResolver: 'resolveQuoteHeaderFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['客户管理', '报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  customer_contact: {
    storagePath: 'Quote.customer.contact',
    valueResolver: 'resolveQuoteHeaderFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['客户管理', '报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  customer_phone: {
    storagePath: 'Quote.customer.phone',
    valueResolver: 'resolveQuoteHeaderFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['客户管理', '报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  valid_until: {
    storagePath: 'Quote.validUntil',
    valueResolver: 'resolveQuoteSystemFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  business_mode: {
    storagePath: 'Quote.businessModeCode / businessModeLabelSnapshot',
    valueResolver: 'resolveQuoteHeaderFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  deposit: {
    storagePath: 'Quote.deposit',
    valueResolver: 'resolveQuoteHeaderFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
  overall_discount: {
    storagePath: 'Quote.overallDiscount',
    valueResolver: 'resolveQuoteHeaderFieldDisplay',
    writeCommand: 'updateQuote',
    placements: ['报价字段池', '报价抬头', '发布快照'],
    snapshotPolicy: 'freeze-on-publication',
    status: 'bound',
  },
};

export function quoteDefinitionBindingForKey(
  key: string,
): BusinessFieldBinding | undefined {
  const binding = QUOTE_DEFINITION_BINDINGS[key];
  const target = OWNER_PERMISSION_TARGETS[ownerForQuoteDefinition(key)];
  return binding && target
    ? {
        ...binding,
        permissionResource: target.featureId,
        readActionId: target.readActionId,
        editActionId: target.editActionId,
        exportActionId: target.exportActionId,
        exportFeatureId: target.exportFeatureId,
      }
    : undefined;
}

export function isQuoteDefinitionBound(key: string): boolean {
  if (/^field\.custom\.new_[1-9]\d*$/.test(key)) return true;
  const status = quoteDefinitionBindingForKey(key)?.status;
  return (
    status === 'bound' ||
    status === 'read-only' ||
    createSystemFieldRegistry().some(
      (descriptor) => descriptor.definition.key === key,
    )
  );
}

/** Stable ids shared by the permission editor and real quote consumers. */
export const QUOTE_SOURCE_FIELDS = {
  quoteId: 'source:Quote.quoteId',
  title: 'definition:quote_title',
  customerSnapshot: 'source:Quote.customer',
  image: 'definition:device_image',
  deviceId: 'source:QuoteItem.deviceId',
  name: 'definition:device_name',
  sku: 'definition:device_sku',
  sourceType: 'source:QuoteItem.sourceType',
  sourceId: 'source:QuoteItem.sourceId',
  sourceName: 'source:QuoteItem.sourceName',
  quantity: 'definition:quantity',
  discount: 'definition:item_discount',
  stock: 'definition:warehouse_stock',
  replacementDeviceId: 'definition:replacement_device',
  remark: 'source:QuoteItem.remark',
  lineTotal: 'definition:product_final_discount_price',
  overallDiscount: 'definition:overall_discount',
  customerName: 'definition:customer_name',
  customerContact: 'definition:customer_contact',
  customerPhone: 'definition:customer_phone',
  validUntil: 'definition:valid_until',
  businessMode: 'definition:business_mode',
  deposit: 'definition:deposit',
} as const;

const EDITABLE_QUOTE_SOURCE_FIELDS = new Set<string>([
  QUOTE_SOURCE_FIELDS.quantity,
  QUOTE_SOURCE_FIELDS.discount,
  QUOTE_SOURCE_FIELDS.replacementDeviceId,
  QUOTE_SOURCE_FIELDS.remark,
  QUOTE_SOURCE_FIELDS.overallDiscount,
  QUOTE_SOURCE_FIELDS.customerSnapshot,
  QUOTE_SOURCE_FIELDS.customerName,
  QUOTE_SOURCE_FIELDS.customerPhone,
  QUOTE_SOURCE_FIELDS.validUntil,
  QUOTE_SOURCE_FIELDS.businessMode,
  QUOTE_SOURCE_FIELDS.deposit,
]);

const EDITABLE_TYPED_SOURCE_FIELDS = new Set<string>([
  permissionFieldIdForDeviceProperty('name'),
  permissionFieldIdForDeviceProperty('sku'),
  permissionFieldIdForDeviceProperty('jkyCode'),
  permissionFieldIdForDeviceProperty('category'),
  permissionFieldIdForDeviceProperty('deviceAttribute'),
  permissionFieldIdForDeviceProperty('procurementAttribute'),
  permissionFieldIdForDeviceProperty('weight'),
  permissionFieldIdForDeviceProperty('sortOrder'),
  permissionFieldIdForDeviceProperty('status'),
  permissionFieldIdForDeviceProperty('remark'),
  permissionFieldIdForDeviceProperty('rentalPrice'),
  permissionFieldIdForDeviceProperty('taxInclusivePlanPrice'),
  permissionFieldIdForDeviceProperty('unitPrice'),
  permissionFieldIdForDeviceProperty('untaxedPlanPrice'),
  permissionFieldIdForDeviceProperty('retailPriceTax13'),
  permissionFieldIdForDeviceProperty('dynamicCost'),
  permissionFieldIdForDeviceProperty('compensationPrice'),
  permissionFieldIdForDeviceProperty('image'),
  permissionFieldIdForDeviceProperty('gallery'),
  permissionFieldIdForDeviceProperty('images'),
  permissionFieldIdForDeviceProperty('primaryImageId'),
  permissionFieldIdForDeviceProperty('replacementIds'),
  permissionFieldIdForDeviceProperty('stockByWarehouse'),
  'source:PackageCatalogRecord.name',
  'source:PackageCatalogRecord.planIds',
  'source:PackageCatalogRecord.sortOrder',
  'source:PackageCatalogRecord.image',
  'source:PackageCatalogRecord.lightImage',
  'definition:package_item_price',
  'definition:package_item_monthly_subtotal',
  'source:PackageProductLink.quantity',
  'source:PlanCatalogRecord.name',
  'definition:scheme_discount',
  'definition:scheme_product_price',
  'definition:scheme_product_subtotal',
  'source:PlanCatalogRecord.sortOrder',
  'source:PlanCatalogRecord.image',
  'source:PlanCatalogRecord.cases',
  'source:CustomerCatalogRecord.customerNo',
  'source:CustomerCatalogRecord.name',
  'source:CustomerCatalogRecord.contact',
  'source:CustomerCatalogRecord.phone',
  'source:CustomerCatalogRecord.manager',
  'source:CustomerCatalogRecord.status',
]);

export function buildPermissionBusinessFieldRows(
  definitions: QuoteFieldDefinitionRecord[],
): PermissionBusinessFieldRow[] {
  const rows = new Map<string, PermissionBusinessFieldRow>();
  for (const field of definitions) {
    const id = `definition:${field.key}`;
    const customOwner =
      field.governance.kind === 'custom' &&
      field.sourceKind === 'manual' &&
      [
        'device',
        'package',
        'plan',
        'customer',
        'quote-header',
        'quote-line',
      ].includes(field.groupKey.replace(/^business:/, ''))
        ? (field.groupKey.replace(/^business:/, '') as
            | 'device'
            | 'package'
            | 'plan'
            | 'customer'
            | 'quote-header'
            | 'quote-line')
        : null;
    const customBinding: BusinessFieldBinding | undefined = customOwner
      ? {
          storagePath:
            customOwner === 'quote-line'
              ? `QuoteItem.manualFieldValues.${field.key}`
              : customOwner === 'quote-header'
                ? `Quote.customFieldValues.${field.key}`
                : `${customOwner === 'device' ? 'DeviceCatalogRecord' : customOwner === 'package' ? 'PackageCatalogRecord' : customOwner === 'plan' ? 'PlanCatalogRecord' : 'CustomerCatalogRecord'}.customFieldValues.${field.key}`,
          valueResolver: 'resolveQuoteSystemFieldDisplay',
          writeCommand: 'updateCustomFieldValue',
          permissionResource:
            customOwner === 'device'
              ? 'devices'
              : customOwner === 'package'
                ? 'packages'
                : customOwner === 'plan'
                  ? 'plans'
                  : customOwner === 'customer'
                    ? 'customers'
                    : 'quotes',
          readActionId:
            customOwner === 'device'
              ? 'devices:catalog.read'
              : customOwner === 'package'
                ? 'packages:solution.read'
                : customOwner === 'plan'
                  ? 'plans:solution.read'
                  : customOwner === 'customer'
                    ? 'customers:customer.read'
                    : 'quotes:quote.read',
          editActionId:
            customOwner === 'device'
              ? 'devices:catalog.manage'
              : customOwner === 'package'
                ? 'packages:solution.manage'
                : customOwner === 'plan'
                  ? 'plans:solution.manage'
                  : customOwner === 'customer'
                    ? 'customers:customer.manage'
                    : 'quotes:quote.edit',
          exportActionId: 'quotes:quote.export',
          placements: ['全字段管理', customOwner, '发布快照'],
          snapshotPolicy: 'freeze-on-publication',
          status: 'bound',
        }
      : undefined;
    rows.set(id, {
      id,
      label: field.label,
      owner: field.groupKey.startsWith('business:')
        ? field.groupKey.slice('business:'.length)
        : ownerForQuoteDefinition(field.key),
      source: 'quote-definition',
      available:
        field.enabled &&
        field.governance.classification !== 'unclassified' &&
        (customBinding !== undefined || isQuoteDefinitionBound(field.key)),
      editable:
        field.enabled &&
        field.sourceKind === 'manual' &&
        field.governance.kind === 'custom' &&
        field.governance.classification !== 'unclassified',
      placements: ['报价字段目录', '报价版本', '通道投影'],
      definitionKey: field.key,
      note: field.note,
      binding: quoteDefinitionBindingForKey(field.key) ??
        customBinding ?? {
          valueResolver: 'none',
          placements: [],
          snapshotPolicy: 'not-applicable',
          status: 'unbound',
        },
    });
  }
  for (const group of READ_ONLY_OBJECT_DIRECTORY) {
    for (const field of group.fields) {
      const id = permissionFieldIdForSource(field.source);
      const existing = rows.get(id);
      if (existing) {
        if (!existing.placements.includes(group.object))
          existing.placements.push(group.object);
        if (
          field.available &&
          (EDITABLE_TYPED_SOURCE_FIELDS.has(id) ||
            EDITABLE_QUOTE_SOURCE_FIELDS.has(id))
        ) {
          existing.editable = true;
          existing.binding = {
            ...existing.binding,
            storagePath: field.source,
            writeCommand:
              existing.binding.writeCommand ?? `save-${group.owner}`,
            placements: [
              ...new Set([...existing.binding.placements, group.object]),
            ],
            status: 'bound',
          };
        }
        continue;
      }
      rows.set(id, {
        id,
        label: field.label,
        owner: group.owner,
        source: 'page',
        available: field.available,
        editable:
          field.available &&
          (EDITABLE_QUOTE_SOURCE_FIELDS.has(id) ||
            EDITABLE_TYPED_SOURCE_FIELDS.has(id)),
        placements: [group.object],
        binding: (() => {
          const target = OWNER_PERMISSION_TARGETS[group.owner];
          if (!field.available || !target)
            return {
              storagePath: field.source,
              valueResolver: 'none',
              placements: [group.object],
              snapshotPolicy: 'not-applicable' as const,
              status: 'unbound' as const,
            };
          return {
            storagePath: field.source,
            valueResolver: 'typed-entity-property',
            permissionResource: target.featureId,
            readActionId: target.readActionId,
            editActionId: target.editActionId,
            exportActionId: target.exportActionId,
            exportFeatureId: target.exportFeatureId,
            placements: [group.object],
            snapshotPolicy:
              group.owner === 'quote-line' || group.owner === 'channel'
                ? ('freeze-on-publication' as const)
                : ('live-master' as const),
            ...(EDITABLE_TYPED_SOURCE_FIELDS.has(id)
              ? { writeCommand: `save-${group.owner}` }
              : {}),
            status:
              EDITABLE_QUOTE_SOURCE_FIELDS.has(id) ||
              EDITABLE_TYPED_SOURCE_FIELDS.has(id)
                ? ('bound' as const)
                : ('read-only' as const),
          };
        })(),
      });
    }
  }
  for (const fieldId of Object.values(LEGACY_SETTINGS_FIELD_BINDINGS)) {
    const row = rows.get(fieldId);
    if (row && !row.placements.includes('兼容设置显式映射')) {
      row.placements.push('兼容设置显式映射');
    }
  }
  // The object registry is the binding source for system facts that do not
  // happen to be quote-column definitions (status, package relations, plan
  // cases, customer manager, etc.). It is merged after compatibility rows so
  // each stable definition id has exactly one owner and one action path.
  for (const descriptor of createUnifiedFieldRegistry(definitions)) {
    const definition = descriptor.definition;
    if (definition.governance.kind === 'custom') continue;
    const id = `definition:${definition.key}`;
    const current = rows.get(id);
    const binding = descriptor.binding;
    rows.set(id, {
      ...(current ?? {
        id,
        source: 'quote-definition' as const,
        placements: [],
        definitionKey: definition.key,
        note: definition.note,
      }),
      label: definition.label,
      owner: descriptor.owner,
      available:
        definition.enabled &&
        definition.governance.classification !== 'unclassified',
      editable:
        current?.editable ??
        (Boolean(binding.editAction) && !definition.systemLocked),
      binding: {
        storagePath: binding.storagePath,
        valueResolver: binding.calculationRef
          ? 'resolveUnifiedCalculation'
          : 'resolveUnifiedFieldValue',
        writeCommand: binding.writeCommand,
        permissionResource: binding.permissionResource,
        readActionId: binding.viewAction,
        editActionId: binding.editAction,
        exportActionId: binding.exportAction,
        placements: [...new Set([...binding.placements, '字段中心'])],
        snapshotPolicy:
          binding.snapshotPolicy === 'live-master'
            ? 'live-master'
            : 'freeze-on-publication',
        calculationRef: binding.calculationRef,
        status: binding.writeCommand ? 'bound' : 'read-only',
      },
    });
  }
  return [...rows.values()];
}
